import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Megaphone, RefreshCw, Plus, Send, Edit, Trash2,
  Eye, EyeOff, Tag
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeMarketing() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';

  // Tab state
  const [activeTab, setActiveTab] = useState("campaigns");

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ name: "", subject: "", content: "", segment: "all" });

  // Popups
  const [popups, setPopups] = useState([]);
  const [loadingPopups, setLoadingPopups] = useState(false);
  const [showPopupDialog, setShowPopupDialog] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [popupForm, setPopupForm] = useState({
    name: "",
    popup_type: "promotion",
    title: "",
    content: "",
    cta_text: "",
    cta_link: "",
    image_url: "",
    target_users: "all",
    target_pages: ["home"],
    show_once: false,
    show_dont_show_again: true,  // Show "Don't show again" checkbox
    max_views: 5,  // Default max views
    delay_seconds: 0,
    is_active: true,
    dismissible: true,
    position: "center"
  });

  // Promo Codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: "",
    discount_percent: 20,
    applies_to_plans: ["monthly", "monthly_yearly"],
    max_uses: null,
    expires_at: "",
    description: ""
  });

  useEffect(() => {
    if (activeTab === "campaigns") fetchCampaigns();
    if (activeTab === "popups") fetchPopups();
    if (activeTab === "promos") fetchPromoCodes();
  }, [activeTab]);
// Fetch functions
  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const res = await axios.get(`${API}/cms/campaigns`, { withCredentials: true });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const fetchPopups = useCallback(async () => {
    setLoadingPopups(true);
    try {
      const res = await axios.get(`${API}/cms/popups`, { withCredentials: true });
      setPopups(res.data.popups || []);
    } catch (error) {
      console.error("Failed to fetch popups:", error);
    } finally {
      setLoadingPopups(false);
    }
  }, []);

  const fetchPromoCodes = useCallback(async () => {
    setLoadingPromos(true);
    try {
      const res = await axios.get(`${API}/super-admin/promo-codes`, { withCredentials: true });
      setPromoCodes(res.data.promo_codes || []);
    } catch (error) {
      console.error("Failed to fetch promo codes:", error);
    } finally {
      setLoadingPromos(false);
    }
  }, []);

  // Campaign functions
  const createCampaign = async () => {
    if (!campaignForm.name || !campaignForm.subject) {
      toast.error("Name and subject are required");
      return;
    }
    try {
      await axios.post(`${API}/cms/campaigns`, campaignForm, { withCredentials: true });
      toast.success("Campaign created!");
      setShowCampaignDialog(false);
      setCampaignForm({ name: "", subject: "", content: "", segment: "all" });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const sendCampaign = async (campaignId) => {
    if (!window.confirm("Send this campaign now to all selected users?")) return;
    try {
      const res = await axios.post(`${API}/cms/campaigns/${campaignId}/send`, {}, { withCredentials: true });
      toast.success(`Campaign sent to ${res.data.sent_count} users!`);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to send campaign");
    }
  };

  // Popup functions
  const savePopup = async () => {
    if (!popupForm.name || !popupForm.title) {
      toast.error("Name and title are required");
      return;
    }
    try {
      if (editingPopup) {
        await axios.put(`${API}/cms/popups/${editingPopup.popup_id}`, popupForm, { withCredentials: true });
        toast.success("Popup updated!");
      } else {
        await axios.post(`${API}/cms/popups`, popupForm, { withCredentials: true });
        toast.success("Popup created!");
      }
      setShowPopupDialog(false);
      setEditingPopup(null);
      resetPopupForm();
      fetchPopups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save popup");
    }
  };

  const togglePopupActive = async (popup) => {
    try {
      await axios.put(`${API}/cms/popups/${popup.popup_id}`, {
        ...popup,
        is_active: !popup.is_active
      }, { withCredentials: true });
      toast.success(popup.is_active ? "Popup deactivated" : "Popup activated");
      fetchPopups();
    } catch (error) {
      toast.error("Failed to update popup");
    }
  };

  const deletePopup = async (popupId) => {
    if (!window.confirm("Are you sure you want to delete this popup?")) return;
    try {
      await axios.delete(`${API}/cms/popups/${popupId}`, { withCredentials: true });
      toast.success("Popup deleted!");
      fetchPopups();
    } catch (error) {
      toast.error("Failed to delete popup");
    }
  };

  const openEditPopup = (popup) => {
    setEditingPopup(popup);
    setPopupForm({
      name: popup.name,
      popup_type: popup.popup_type,
      title: popup.title,
      content: popup.content || "",
      cta_text: popup.cta_text || "",
      cta_link: popup.cta_link || "",
      image_url: popup.image_url || "",
      target_users: popup.target_users || "all",
      target_pages: popup.target_pages || ["home"],
      show_once: popup.show_once !== false,
      delay_seconds: popup.delay_seconds || 0,
      is_active: popup.is_active !== false,
      dismissible: popup.dismissible !== false,
      show_dont_show_again: popup.show_dont_show_again !== false,
      max_views: popup.max_views ?? 5,
      position: popup.position || "center"
    });
    setShowPopupDialog(true);
  };

  const resetPopupForm = () => {
    setPopupForm({
      name: "",
      popup_type: "promotion",
      title: "",
      content: "",
      cta_text: "",
      cta_link: "",
      image_url: "",
      target_users: "all",
      target_pages: ["home"],
      show_once: false,
      show_dont_show_again: true,
      max_views: 5,
      delay_seconds: 0,
      is_active: true,
      dismissible: true,
      position: "center"
    });
  };

  // Promo Code functions
  const createPromoCode = async () => {
    if (!promoForm.code) {
      toast.error("Promo code is required");
      return;
    }
    try {
      await axios.post(`${API}/super-admin/promo-codes`, {
        ...promoForm,
        expires_at: promoForm.expires_at || null
      }, { withCredentials: true });
      toast.success("Promo code created!");
      setShowPromoDialog(false);
      setPromoForm({
        code: "",
        discount_percent: 20,
        applies_to_plans: ["monthly", "monthly_yearly"],
        max_uses: null,
        expires_at: "",
        description: ""
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm("Deactivate this promo code?")) return;
    try {
      await axios.delete(`${API}/super-admin/promo-codes/${promoId}`, { withCredentials: true });
      toast.success("Promo code deactivated");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to deactivate promo code");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No expiry';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Marketing</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email campaigns, popups, and promotions</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`flex gap-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        {[
          { id: "campaigns", label: "Email Campaigns", icon: Mail },
          { id: "popups", label: "Popups & Banners", icon: Megaphone },
          { id: "promos", label: "Promo Codes", icon: Tag },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-violet-600 text-violet-600'
                : `border-transparent ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Email Campaigns Tab */}
      {activeTab === "campaigns" && (
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Campaigns</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchCampaigns}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => setShowCampaignDialog(true)}>
                <Plus className="w-4 h-4 mr-1" /> New Campaign
              </Button>
            </div>
          </div>

          {loadingCampaigns ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <Mail className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No campaigns yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => (
                <div key={c.campaign_id} className={`flex items-center justify-between p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{c.subject}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      c.status === "sent" ? "bg-green-100 text-green-700" :
                      c.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{c.status}</span>
                    {c.status === "draft" && (
                      <Button size="sm" onClick={() => sendCampaign(c.campaign_id)}>
                        <Send className="w-3 h-3 mr-1" /> Send
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Popups Tab */}
      {activeTab === "popups" && (
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Popups & Banners</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchPopups}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => {
                setEditingPopup(null);
                resetPopupForm();
                setShowPopupDialog(true);
              }}>
                <Plus className="w-4 h-4 mr-1" /> New Popup
              </Button>
            </div>
          </div>

          {loadingPopups ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            </div>
          ) : popups.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No popups yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {popups.map(popup => (
                <div key={popup.popup_id} className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{popup.name}</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{popup.title}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded text-xs bg-violet-100 text-violet-700">
                          {popup.popup_type}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${popup.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {popup.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {popup.max_views > 0 && (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                            Max {popup.max_views} views
                          </span>
                        )}
                        {popup.delay_seconds > 0 && (
                          <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                            {popup.delay_seconds}s delay
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => togglePopupActive(popup)}>
                        {popup.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditPopup(popup)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deletePopup(popup.popup_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Promo Codes Tab */}
      {activeTab === "promos" && (
        <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Promo Codes</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchPromoCodes}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => setShowPromoDialog(true)}>
                <Plus className="w-4 h-4 mr-1" /> New Promo
              </Button>
            </div>
          </div>

          {loadingPromos ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="text-center py-12">
              <Tag className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No promo codes yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map(promo => (
                <div key={promo.promo_id} className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{promo.code}</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {promo.discount_percent}% off • {promo.times_used || 0} uses • Expires: {formatDate(promo.expires_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deletePromoCode(promo.promo_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Dialogs */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Email Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Campaign Name" value={campaignForm.name} onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})} />
            <Input placeholder="Email Subject" value={campaignForm.subject} onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})} />
            <textarea 
              className={`w-full p-3 border rounded-lg ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
              rows={4} 
              placeholder="Email content (HTML supported)" 
              value={campaignForm.content} 
              onChange={(e) => setCampaignForm({...campaignForm, content: e.target.value})} 
            />
            <Select value={campaignForm.segment} onValueChange={(v) => setCampaignForm({...campaignForm, segment: v})}>
              <SelectTrigger><SelectValue placeholder="Target Audience" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="trial">Trial Users</SelectItem>
                <SelectItem value="paid">Paid Users</SelectItem>
                <SelectItem value="inactive">Inactive Users</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Cancel</Button>
              <Button onClick={createCampaign}>Create Campaign</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPopupDialog} onOpenChange={setShowPopupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPopup ? 'Edit Popup' : 'Create Popup'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <Input placeholder="Popup Name (internal)" value={popupForm.name} onChange={(e) => setPopupForm({...popupForm, name: e.target.value})} />
            <Select value={popupForm.popup_type} onValueChange={(v) => setPopupForm({...popupForm, popup_type: v})}>
              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="promotion">Promotion</SelectItem>
                <SelectItem value="announcement">Announcement</SelectItem>
                <SelectItem value="newsletter">Newsletter Signup</SelectItem>
                <SelectItem value="feedback">Feedback</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Title" value={popupForm.title} onChange={(e) => setPopupForm({...popupForm, title: e.target.value})} />
            <textarea 
              className={`w-full p-3 border rounded-lg ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
              rows={3} 
              placeholder="Content (HTML supported)" 
              value={popupForm.content} 
              onChange={(e) => setPopupForm({...popupForm, content: e.target.value})} 
            />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="CTA Text" value={popupForm.cta_text} onChange={(e) => setPopupForm({...popupForm, cta_text: e.target.value})} />
              <Input placeholder="CTA Link" value={popupForm.cta_link} onChange={(e) => setPopupForm({...popupForm, cta_link: e.target.value})} />
            </div>
            <Input placeholder="Image URL (optional)" value={popupForm.image_url} onChange={(e) => setPopupForm({...popupForm, image_url: e.target.value})} />
            <Select value={popupForm.position} onValueChange={(v) => setPopupForm({...popupForm, position: v})}>
              <SelectTrigger><SelectValue placeholder="Position" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                <SelectItem value="top-center">Top Banner</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Display Configuration */}
            <div className={`border rounded-lg p-4 space-y-3 ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Display Configuration</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Max Views (0 = unlimited)</label>
                  <Input 
                    type="number" 
                    min="0" 
                    max="100"
                    value={popupForm.max_views} 
                    onChange={(e) => setPopupForm({...popupForm, max_views: parseInt(e.target.value) || 0})} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Delay (seconds)</label>
                  <Input 
                    type="number" 
                    min="0" 
                    max="60"
                    value={popupForm.delay_seconds} 
                    onChange={(e) => setPopupForm({...popupForm, delay_seconds: parseInt(e.target.value) || 0})} 
                  />
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={popupForm.show_dont_show_again}
                    onChange={(e) => setPopupForm({...popupForm, show_dont_show_again: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Show &quot;Don&apos;t show again&quot; checkbox</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={popupForm.show_once}
                    onChange={(e) => setPopupForm({...popupForm, show_once: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Show only once (never again after first view)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={popupForm.dismissible}
                    onChange={(e) => setPopupForm({...popupForm, dismissible: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Allow dismissal (show X button)</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPopupDialog(false)}>Cancel</Button>
              <Button onClick={savePopup}>{editingPopup ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="PROMO_CODE" value={promoForm.code} onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})} />
            <div>
              <label className="block text-sm font-medium mb-1">Discount %</label>
              <Input type="number" min="1" max="100" value={promoForm.discount_percent} onChange={(e) => setPromoForm({...promoForm, discount_percent: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Uses (empty = unlimited)</label>
              <Input type="number" min="1" value={promoForm.max_uses || ''} onChange={(e) => setPromoForm({...promoForm, max_uses: e.target.value ? parseInt(e.target.value) : null})} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Expires At</label>
              <Input type="date" value={promoForm.expires_at} onChange={(e) => setPromoForm({...promoForm, expires_at: e.target.value})} />
            </div>
            <Input placeholder="Description (optional)" value={promoForm.description} onChange={(e) => setPromoForm({...promoForm, description: e.target.value})} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPromoDialog(false)}>Cancel</Button>
              <Button onClick={createPromoCode}>Create Promo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
